/** SUNTRONIX ONE — Enterprise OS Home. TODAY → NEEDS ATTENTION → ACTION → PERFORMANCE. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import CommandBar from "@/components/app/CommandBar";
import CopilotBox from "@/components/app/CopilotBox";
import AskSuntronixAI from "@/components/askai/AskSuntronixAI";
import AlertsStrip from "@/components/alerts/AlertsStrip";
import {
  TodayHeader, AttentionPanel, ActionPanel, PerformancePanel, FeedPanel, SectionLabel,
} from "./osHomeParts";

export default function OsHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/os-home/summary");
      setData(data);
      setRefreshedAt(new Date());
    } catch { if (!silent) toast.error("Could not load your home"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const runAction = async (action) => {
    if (action.kind === "route") { navigate(action.route); return; }
    try {
      await api.request({ method: action.api.method, url: action.api.url, data: action.api.body });
      toast.success("Done");
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const complete = async (item) => {
    await api.post("/one-today/complete", { key: item.key });
    toast.success("Marked done");
    load(true);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500" data-testid="os-home-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Building your day…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-8" data-testid="os-home">
      <TodayHeader user={data.user} date={data.date} counts={data.attention.counts}
        refreshedAt={refreshedAt} onRefresh={() => load(true)} />

      <AskSuntronixAI compact />
      <AlertsStrip businessOnly />
      <CopilotBox />
      <div data-testid="os-home-search-wrap">
        <CommandBar testId="os-home-search" />
      </div>

      <section data-testid="os-attention">
        <SectionLabel step="02" title="What needs attention" count={data.attention.counts.action_items}
          onMore={() => navigate("/today")} moreLabel="Open My Action Centre" />
        <AttentionPanel attention={data.attention} onAction={runAction} onComplete={complete} />
      </section>

      <section data-testid="os-action">
        <SectionLabel step="03" title="Action" />
        <ActionPanel actions={data.actions} approvals={data.attention.approvals} onAction={runAction} />
      </section>

      <section data-testid="os-performance">
        <SectionLabel step="04" title="Performance" hint="Month to date · live" />
        <PerformancePanel groups={data.performance} />
      </section>

      <section data-testid="os-feed">
        <SectionLabel step="05" title="Live feed" />
        <FeedPanel feed={data.feed} isManagement={data.user.is_management} />
      </section>
    </div>
  );
}
